import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { AdminTabs, ComparablesTabs, MainProjectTabs, ModulesEnum, ProjectValuationTabs, ModulesSvgEnum } from '../enum';
import { NavigationService } from '../navigation.service';
import { NavigationBase } from '../navigation-base';
import { Observable, take } from 'rxjs';
import { BusinessUnitService } from 'src/app/modules/app-settings/services/business-unit.service';
import { AppStorageService } from '../../services/app-storage.service';
import { RaciNavigationService } from '../raci-navigation.service';
import { NavigationStart, Router } from '@angular/router';
import { EventBusService } from '../../services/event-bus.service';
import { HttpClient } from '@angular/common/http';
import { AppServiceHelperService } from '../../services/app-service-helper.service';
import { AppLoadingIndicatorService } from '../../services/app-loading-indicator.service';

/**
 * Top main navigation: Hosts the module specific navigation components
 */
@Component({
  selector: 'top-main-navigation',
  templateUrl: './top-main-navigation.component.html',
  styleUrls: ['./top-main-navigation.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class TopMainNavigationComponent extends NavigationBase implements OnInit {
  /**
   * ChangeDetectorRef
   */
  _ref: ChangeDetectorRef;
  /**
   * Search tab visibility
   */
  searchTabVisible = false;
  /**
   * Search mode
   */
  searchMode = 'projectstd';
  /**
   * Hide RACI
   */
  hideRaci: boolean;
  /**
   * Dashboard inactive
   */
  dashboardInactive = false;
  /**
   * tenant inactive
   */
  tenantInactive = false;
  /**
   * Valuation inactive
   */
  valuationInactive = false;
  /**
   * Property inactive
   */
  propertyInactive = false;
  /**
   * Tasks count
   */
  tasksCount$: Observable<number>;

  /**
   * Indicate is localizatio host area
   */
  isLocalizationHostArea = false;

  /**
   * Indicate is localization area
   */
  isLocalizationArea = false;

  /**
   * Selected tab
   */
  selectedTab: any = ProjectValuationTabs.Projects;

  /**
   * Is admin
   */
  isAdmin = false;

  /**
   * Is business analyst
   */
  isBusinessAnalyst = false;

  /**
   * Is calculation model area
   */
  isCalculationModelArea = false;

  /**
   * Is raci template area
   */
  isRaciTemplateArea = false;

  /**
   * Link counter
   */
  linkCounter: any = {
    property: 0,
    report: 0,
    valuationPortfolio: 0,
    project: 0
  };

  /**
   * Active module
   */
  activeModule: ModulesEnum;

  /**
   * ModulesSvgEnum
   */
  modulesSvg = ModulesSvgEnum;

  /**
   * Active svg
   */
  activeSvg = '';

  /**
   * Show
   */
  public show = false;

  /**
   * The user's Business Units
   */
  public userBusinessUnits: any[] = null;

  /**
   * The current user's BU
   */
  public userCurrentBusinessUnit = null;

  /**
   * The user is admin or has more than one BU? Show the BU selector
   */
  public isAdminOrMoreThanOneBu = false;

  /**
   * Constructor
   * @param navigationService NavigationService
   * @param ref ChangeDetectorRef
   */
  constructor(eventBus: EventBusService,
    private appStorage: AppStorageService,
    private navigationService: NavigationService,
    ref: ChangeDetectorRef,
    private buService: BusinessUnitService,
    private raciNavigationService: RaciNavigationService,
    private router: Router,
    private http: HttpClient,
    private appServiceHelper: AppServiceHelperService,
    private loadingIndicator: AppLoadingIndicatorService
  ) {
    super(eventBus, ref);
    this._ref = ref;

    this.router.events.subscribe((event) => {
      if (event instanceof NavigationStart) {
        if (this.router.url.startsWith('/rd') && !event.url.startsWith('/rd')) {
          this.eventBus.broadcastEvent('rd:navigateFromReportDesignerStarted');
        } else if (event.url.startsWith('/rd')) {
          this.eventBus.broadcastEvent('rd:navigateToReportDesignerStarted');
        }
      }
    });
  }

  /**
   * Gets the ModulesEnum enum
   */
  public get moduleType(): typeof ModulesEnum {
    return ModulesEnum;
  }

  /**
   * Initializes the component
   */
  ngOnInit(): void {
    this.handleNavigationState();
    this.subscribeToActiveAdminTab();
    this.subscribeToActiveMainProjectTab();
    this.subscribeToActiveProjectValuationTab();
    this.subscribeToComparablesTab();
    this.tasksCount$ = this.navigationService.pendingTasksCount$;
    this.isAdmin = this.appStorage.userIsInRole(['Admin']);
    this.isBusinessAnalyst = this.appStorage.userIsInRole(['Business Analyst']);
    this._ref.markForCheck();

    this.subscriptions.add(this.navigationService.activeModule$.subscribe((module: ModulesEnum) => {
      setTimeout(() => {
        this.activeModule = module;
        this.activeSvg = this.modulesSvg[module];
        this._ref.markForCheck();
      });
    }));

    this.onEvents.push(this.eventBus.subscribeEvent('setCounterValueBroadcast', (tab, value) => {
      this.linkCounter[tab] = value;
      this._ref.markForCheck();
    }));

    this.onEvents.push(this.eventBus.subscribeEvent('resetLinkCounter', () => {
      this.linkCounter['report'] = 0;
      this.linkCounter['property'] = 0;
      this._ref.markForCheck();
    }));

    this.onEvents.push(this.eventBus.subscribeEvent('appStorage:setSelectedProject', (selectedProject) => {
      if (selectedProject) {
        this.hideRaci = !(selectedProject.HasParticipants && selectedProject.HasProperties);
      }
    }));

    this.http.get<any>(this.appServiceHelper.getServiceEndpoint('pollux.settings.api', 'user/businessUnits')).pipe(take(1)).subscribe((buResult) => {
      const user = this.appStorage.getCurrentUserDetails();
      this.userBusinessUnits = [];
      if (this.appStorage.userIsInRole(['Admin'])) {
        this.getAdminBUs(buResult, user);
      }
      else {
        this.getNonAdminBUs(user);
      }
      this.userCurrentBusinessUnit = user.UserBU;
    });
  }

  /**
   * Gets the BUs for a non-admin
   * @param user
   */
  private getNonAdminBUs(user: any): void {
    for (const bu of user.BusinessUnits.filter((b) => b.oid !== 0 && b.active)) {
      this.userBusinessUnits.push({
        oid: bu.oid,
        description: bu.displayName,
        directAssignment: true
      });
    }
    if (this.userBusinessUnits.length > 1) {
      this.isAdminOrMoreThanOneBu = true;
    }
  }

  /**
   * Gets all BUs for an admin
   * @param buResult
   * @param user
   */
  private getAdminBUs(buResult: any, user: any): void {
    for (const bu of buResult.filter((b) => b.oid !== 0 && b.active)) {
      const directlyAssignedBu = user.BusinessUnits.filter((userBu) => { return bu.oid === userBu.oid; });
      this.userBusinessUnits.push({
        oid: bu.oid,
        description: bu.displayName,
        directAssignment: directlyAssignedBu.length !== 0
      });
    }
    this.isAdminOrMoreThanOneBu = true;
  }

  /**
   * Change handler for the active BU selector
   */
  public activeBusinessUnitChanged() {
    this.loadingIndicator.show();
    const url = this.appServiceHelper.getServiceEndpoint('pollux.api', `user/bu?buId=${this.userCurrentBusinessUnit}`);
    this.http.post(url, {}).pipe(take(1)).subscribe((result: any) => {
      if (!result.success) {
        alert(result.message); //Shouldn't happen, but just to be safe
      }
      this.loadingIndicator.hide();
      this.eventBus.broadcastEvent("ActiveBusinessUnitChanged");
    });
  }

  /**
   * Subscribe to active project valuation tab
   */
  subscribeToActiveProjectValuationTab() {
    this.subscriptions.add(this.navigationService.activeProjectValuationTab$.subscribe(tab => {
      this.selectedTab = tab;
      this.isCalculationModelArea = this.navigationService.isCalculationModelArea();
      this._ref.markForCheck();
    }));
  }
  /**
   * Subscribe to active admin tab
   */
  subscribeToActiveAdminTab() {
    this.subscriptions.add(this.navigationService.activeAdminTab$.subscribe(tab => {
      this.selectedTab = tab;
      this.isCalculationModelArea = this.navigationService.isCalculationModelArea();
      this.isRaciTemplateArea = this.navigationService.isRaciTemplateArea();
      this.isLocalizationArea = this.navigationService.isLocalizationArea();
      this._ref.markForCheck();
    }));
  }
  /**
   * Subscribe to active main project tab
   */
  subscribeToActiveMainProjectTab() {
    this.subscriptions.add(this.navigationService.activeMainProjectTab$.subscribe(tab => {
      this.selectedTab = tab;
      this.isCalculationModelArea = this.navigationService.isCalculationModelArea();
      this._ref.markForCheck();
    }));
  }
  /**
   * Subscribe to comparables tab
   */
  subscribeToComparablesTab() {
    this.subscriptions.add(this.navigationService.activeComparablesTab$.subscribe(tab => {
      this.selectedTab = tab;
      this.isCalculationModelArea = this.navigationService.isCalculationModelArea();
      this._ref.markForCheck();
    }))
  }
  /**
   * Gets the ProjectValuationTabs enum
   */
  public get projectValuationsActiveTab(): typeof ProjectValuationTabs {
    return ProjectValuationTabs;
  }
  /**
   * Gets the MainProjectTabs enum
   */
  public get mainProjectActiveTab(): typeof MainProjectTabs {
    return MainProjectTabs;
  }
  /**
   * Gets the ComparablesTabs
   */
  public get comparablesActiveTab(): typeof ComparablesTabs {
    return ComparablesTabs;
  }
  /**
   * Gets the AdminTabs enum
   */
  public get adminActiveTab(): typeof AdminTabs {
    return AdminTabs;
  }
  /**
   * Handles the navigation state
   */
  handleNavigationState() {
    this.onEvents.push(this.eventBus.subscribeEvent('setCounterValueBroadcast', (tab, value) => {
      const counterIsZero = value <= 0;
      switch (tab) {
        case 'report':
          this.dashboardInactive = counterIsZero;
          break;
        case 'valuation':
          this.valuationInactive = counterIsZero;
          break;
        case 'tenant':
          this.tenantInactive = counterIsZero;
          break;
        case 'property':
          this.propertyInactive = counterIsZero;
          break;
        default:
          return;//not our tab
      }

      this._ref.markForCheck();
    }));
  }
  /**
   * Triggers action
   * @param action
   */
  triggerAction(action: string) {
    this.eventBus.broadcastEvent(action);
  }
  /**
   * Shows popup
   */
  showPopup() {
    if (this.appStorage.getSelectedPortfolio() == null && this.appStorage.getSelectedProject() == null) {
      this.hideRaci = true;
    }

    this.show = true;
  }
  /**
   * Hides popup
   */
  hidePopup() {
    this.show = false;
  }

  /**
   * Add enw BU action handler
   */
  addNewBU() {
    this.buService.setAddNewBUAction();
  }

  /**
   * Go to the project organization module
   */
  goToProjectOrganisation() {
    this.raciNavigationService.onShowHideRaciModule(false);
    this.appStorage.setSelectedProject(null);
    this.appStorage.setSelectedPortfolio(null);
    this.navigationService.setActiveModule(this.moduleType.ProjectValuation);
  }
}
